import React, { ReactElement, useState } from 'react';
import { observer } from 'mobx-react';
import Parser from 'html-react-parser';

import classes from './quiz.module.scss';

import { questions } from '@/data/questions';
import { useStorage } from '@/models/Storage';
import PhoneSVG from '@/assets/svg/phone.svg';
import qrImage from '@/assets/images/qr.png';
import Image from '@/components/UI/Image/Image';
import Button from '@/components/UI/Button/Button';
import useMobile from '@/hooks/useMobile';
import { AnswersList } from '@/components/Quiz/Answers/AnswersList/AnswersList';
import bg1 from '@/assets/images/bg1.png';
import bg2 from '@/assets/images/bg2.png';
import { analytics, prepareUrl } from '@/utils/analytics';

type Option = {
    svg: ReactElement;
    text: string;
    id: string;
    correct: boolean;
};

const errors = [
    'А вот и нет',
    'Попробуйте другой вариант',
    'Неправильно',
    'Это другое',
    'Ошибочка вышла',
    'Подумайте еще',
];

const defaultMessages = [
    'Загляните в&nbsp;бесконечную ленту ярких моментов',
    'Кстати, в&nbsp;Ozon Моментах уже есть ваши любимые блогеры',
    'А в Моментах прямо из&nbsp;этих видео можно покупать!',
    'Кстати, в Моментах удобно выбирать товары',
    'В Моментах делятся публикациями не&nbsp;только блогеры, но&nbsp;и&nbsp;продавцы',
    'В Моментах можно общаться',
    'В Моментах товары продаются через впечатления',
];

export const Quiz = observer(() => {
    const iframeRef = React.useRef(null);
    const listRef = React.useRef(null);
    const mobile = useMobile();

    const { stage, setStage, transition, setTransition, guide, finishGuide } = useStorage();

    const shuffle = (array) => {
        let currentIndex = array.length,
            randomIndex;
        while (currentIndex != 0) {
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex--;
            [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
        }
        return array;
    };

    const [messages, setMessages] = React.useState([]);
    React.useEffect(() => {
        setMessages(shuffle(defaultMessages));
    }, [stage]);

    const [question, setQuestion] = React.useState(0);
    const [correctAnswers, setCorrectAnswers] = React.useState(1);
    const [error, setError] = React.useState(false);
    const [dragging, setDragging] = React.useState(false);

    const [savedOptions, setSavedOptions] = React.useState<Option[]>([]);
    const [questionFinished, setQuestionFinished] = React.useState(false);
    const [quizFinished, setQuizFinished] = React.useState(false);
    const [flash, setFlash] = React.useState(false);
    const [mobileHint, setMobileHint] = React.useState(mobile);

    const [iframeAnimFinished, setIframeAnimFinished] = React.useState(false);
    const [secondFrame, setSecondFrame] = React.useState(false);

    const randomNoRepeats = (array) => {
        var copy = array.slice(0);
        return function () {
            if (copy.length < 1) {
                copy = array.slice(0);
            }
            var index = Math.floor(Math.random() * copy.length);
            var item = copy[index];
            copy.splice(index, 1);
            return item;
        };
    };

    const errorGenerator = randomNoRepeats(errors);

    const reset = () => {
        setSavedOptions([]);
        setDragging(false);
        setQuestionFinished(false);
        setCorrectAnswers(1);
        setIframeAnimFinished(false);
        setSecondFrame(false);
    };

    const finish = () => {
        setQuizFinished(true);
        setStage('outro');
        reset();
        setQuestion(0);
    };

    React.useEffect(() => {
        let timer;
        if (error) {
            timer = setTimeout(() => {
                setError(false);
            }, 1000);
        }
        return () => clearTimeout(timer);
    }, [error]);

    React.useEffect(() => {
        let timer;
        if (mobileHint) {
            timer = setTimeout(() => {
                setMobileHint(false);
            }, 4750);
        }
        return () => clearTimeout(timer);
    }, [mobileHint]);

    React.useEffect(() => {
        setMobileHint(mobile);
    }, [mobile]);

    React.useEffect(() => {
        if (questions.length <= question + 1 && questionFinished) {
            finish();
        }
    }, [questionFinished]);

    React.useEffect(() => {
        let timer;
        if (savedOptions.length === correctAnswers) {
            setSecondFrame(true);
        }
        timer = setTimeout(
            () => {
                setQuestionFinished(savedOptions.length === correctAnswers);
            },
            savedOptions.length === correctAnswers ? 1000 : 0,
        );
        return () => clearTimeout(timer);
    }, [savedOptions]);

    React.useEffect(() => {
        const answers = questions[question].options;
        setCorrectAnswers(answers.filter((option) => option.correct).length);
    }, [question]);

    React.useEffect(() => {
        if (stage === 'quiz') {
            analytics(questions[question].adFox);
        }
    }, [question, stage]);

    const nextQuestion = () => {
        if (questions.length > question + 1) {
            // console.log('next question');
            setQuestion(question + 1);
            setQuestionFinished(false);
            reset();
        } else {
            finish();
        }
    };

    const onDrop = (id?: string) => {
        if (!id) {
            setDragging(false);
            return;
        }
        const draggedOption = questions[question]?.options.find((option) => option.id === id);

        if (
            draggedOption.correct &&
            !savedOptions.find((option) => draggedOption.id === option.id)
        ) {
            const arr = [...savedOptions];
            arr.push(draggedOption);
            listRef.current.triggerSuccess(id);
            setFlash(true);
            setTimeout(() => {
                setSavedOptions(arr);
            }, 750);
        } else {
            listRef.current.triggerError(id);
            // console.log(`Error in answer ${id}`);
            setError(true);
        }
        setDragging(false);
    };

    const lastQuestion = question === questions.length - 1;

    return (
        <section
            id={'quiz'}
            className={`${classes.quizSection} ${classes[stage]} ${guide ? classes.guide : ''}`}
            onTransitionEnd={() => {
                setTransition(false);
            }}
        >
            {stage !== 'quiz' && !transition ? null : (
                <>
                    <div className={classes.wrapper}>
                        <div className={classes.content}>
                            <div className={classes.ui}>
                                {mobileHint ||
                                (questionFinished && mobile && !lastQuestion) ||
                                !mobile ? (
                                    <div className={classes.text}>
                                        <span className={classes.title}>
                                            {questionFinished
                                                ? 'Вы угадали! Продолжим?'
                                                : 'Посмотрите момент'}
                                        </span>
                                        <p>
                                            {questionFinished
                                                ? Parser(
                                                      messages[question] ? messages[question] : '',
                                                  )
                                                : 'Попробуйте угадать, о чем в нем идет речь. Для этого перетяните предметы прямо на видео.'}
                                        </p>
                                        {mobile && questionFinished && (
                                            <div className={classes.buttons}>
                                                <Button
                                                    text={'В Моменты'}
                                                    link={prepareUrl(questions[question].link)}
                                                    colorScheme={'secondary'}
                                                />
                                                <Button
                                                    onClick={nextQuestion}
                                                    text={'Далее'}
                                                    arrow
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                                {!questionFinished
                                    ? !mobileHint && (
                                          <AnswersList
                                              onDrop={onDrop}
                                              onDrag={() => {
                                                  setFlash(false);
                                                  setDragging(true);
                                              }}
                                              ref={listRef}
                                              className={classes.list}
                                              question={questions[question]}
                                          />
                                      )
                                    : !mobile && (
                                          <Button
                                              onClick={nextQuestion}
                                              arrow
                                              text={
                                                  mobile || lastQuestion
                                                      ? 'Далее'
                                                      : 'Следующий момент'
                                              }
                                          />
                                      )}
                            </div>
                            <div className={classes.video}>
                                <Image src={bg1} className={`${classes.bg1} ${classes.mobile}`} />
                                <Image src={bg2} className={`${classes.bg2} ${classes.mobile}`} />
                                <div className={classes.circle} />
                                <div className={classes.phone}>
                                    <div
                                        className={`${classes.phoneWrapper} ${
                                            flash ? classes.anim : ''
                                        }`}
                                    >
                                        <PhoneSVG />
                                        <div className={classes.embed}>
                                            {!iframeAnimFinished && (
                                                <iframe
                                                    ref={iframeRef}
                                                    width="320"
                                                    height="690"
                                                    src={`https://ozon.ru/social-embed?post_id=${questions[question].iframeURL}&hide-title=true&hide-products=true&hide-actions=true`}
                                                    title="Ozon Моменты"
                                                    frameBorder="0"
                                                    allow={`accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture`}
                                                    allowFullScreen
                                                />
                                            )}
                                            {secondFrame && (
                                                <iframe
                                                    onAnimationEnd={() => {
                                                        setIframeAnimFinished(true);
                                                    }}
                                                    className={classes.iframeNew}
                                                    width="320"
                                                    height="690"
                                                    src={`https://ozon.ru/social-embed?post_id=${questions[question].iframeURL}`}
                                                    title="Ozon Моменты"
                                                    frameBorder="0"
                                                    allow={`accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture`}
                                                    allowFullScreen
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <>
                                    <div
                                        id={'area'}
                                        className={`${classes.area} ${
                                            dragging || error ? classes.visible : ''
                                        } ${error ? classes.error : ''}`}
                                    >
                                        <span>{error ? errorGenerator() : 'Перетяните сюда'}</span>
                                    </div>

                                    <div
                                        className={`${classes.drop} ${
                                            dragging || error ? classes.visible : ''
                                        } ${secondFrame || questionFinished ? classes.hidden : ''}`}
                                    >
                                        {[...Array(correctAnswers)].map((x, index) => {
                                            return (
                                                <div
                                                    key={'correct_' + index}
                                                    className={`${
                                                        savedOptions[index]?.text
                                                            ? classes.full
                                                            : ''
                                                    } droppoint`}
                                                >
                                                    {savedOptions[index]?.text}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                                <span className={classes.count}>
                                    {question + 1}/{questions.length}
                                </span>
                            </div>
                        </div>
                        <div className={classes.qr}>
                            <p>Смотреть моменты</p>
                            <Image src={qrImage} />
                        </div>
                    </div>
                </>
            )}
        </section>
    );
});
